import { useContext, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  Anchor,
  Box,
  Icon,
  IconButton,
  Menu,
  Table,
  Tag,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import {
  IconChevronDownCircleFilled,
  IconChevronUpCircleFilled,
  IconCircleFilled,
  IconMoreFilled,
  IconWarningFilled,
} from '@zillow/constellation-icons';

import { CometUserRoleType } from '@zg-rentals/rental-manager-env';

import { LoadingImage, LoadingText } from '@zg-rentals/loading-components';

import { AccountViewListingGroupType, AccountViewListingStatus } from '@zg-rentals/ts-rental-manager-api-beta-client';

import {
  useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { transformFiltersToApiParams } from '../../../hooks/queries/zram';
import { useGetPropertyLeases } from '../../../hooks/queries/properties/useGetPropertyLeases';
import { useGetPropertiesInquiries } from '../../../hooks/queries/properties/useGetPropertiesInquiries';
import { useGetPropertiesPayment } from '../../../hooks/queries/properties/useGetPropertiesPayment';

import { AppSelectors, PropertiesSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import PropertiesActions from '../../../actions/PropertiesActions';

import { RMWContext } from '../../../providers/RMWContext';
import stringHelper from '../../../utils/stringHelper';
import { RMW_SUB_PATH } from '../../../pathsManifest';
import { escapeRoute, PANEL_ID } from '../../../utils/escapeRoute';
import PropertyConstants from '../../../constants/PropertyConstants';
import { getPhotoUrls } from '../../../utils/propertiesPageUtils';

import PropertyPanel from '../../../model/PropertyPanel';

import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import ManageTenantsCSEvents from '../../../model/ClickstreamEvent/ManageTenantsCSEvents';
import MultiplexCSEvents from '../../../model/ClickstreamEvent/MultiplexCSEvents';

import EnableApplicationsModal from '../../Modal/EnableApplicationsModal';

import MidwestStyleHouse2x from '../../../images/MidwestStyleHouse@2x.svg';

import {
  CELL_DIMENSIONS,
  cellAddressContainer,
  cellAnchorStyles,
  flexContainerStyles,
  HiddenMobileTableCell,
  leftDataCellStyles,
  propertyPhotoContainerStyles,
  propertyPhotoRecipe,
  rightDataCellStyles,
  TableCell,
  tagStyles,
} from './styles';

import { minWidthZero, singleTextLineStyles } from '../../../styles';

import type React from 'react';
import type { TagPropsInterface } from '@zillow/constellation-10';

import type {
  AccountViewListingGroupDto,
  AccountViewTableCellCtaDto,
  AccountViewTableCellDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type { PanelProps } from '../../../model/PropertyPanel';

type GroupablePropertyRowProps = {
  index: number;
  listing: AccountViewListingGroupDto;
  isLoading?: boolean;
  showMoreListingAlias: string;
  setShowMoreListingAlias: (listingAlias: string) => void;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
  rowStyle?: React.CSSProperties;
  isScrolledToLeft?: boolean;
  onExpandGroupClick?: ({ index, groupId }: { index: number; groupId: string }) => void;
  isRowOpen?: boolean;
  isFetching?: boolean;
};

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const GroupablePropertyRow = ({
  index,
  listing,
  isLoading = false,
  showMoreListingAlias,
  isScrolledToLeft = false,
  onExpandGroupClick,
  onUpsellCellHover,
  rowStyle,
  setShowMoreListingAlias,
  isRowOpen = false,
  isFetching = false,
}: GroupablePropertyRowProps) => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const [displayApplicationsModal, setDisplayApplicationsModal] = useState(false);

  const cellWidth = CELL_DIMENSIONS.WIDTH;

  const textMaxWidth = {
    base: `${cellWidth + 100}px`,
    lg: `${cellWidth}px`,
    xxl: `${cellWidth + 50}px`,
  };

  const {
    canAccessDashboardRedesign,
    constellationScreenSize,
    isPostingPathAdmin,
    user,
    isScreenLg,
    isScreenXlGTE,
    isRunningInProd,
    searchText,
    includeActive,
    includeInactive,
    includeArchived,
    includeUnarchived,
  } = useSelector((state) => {
    const propertiesFilters = PropertiesSelectors.makePropertiesFilters(state);
    const searchText = propertiesFilters.searchText ?? '';
    const subNavFilterType = propertiesFilters.subNavFilterType ?? 'all';
    const apiParams = transformFiltersToApiParams({ searchText, subNavFilterType });
    return {
      canAccessDashboardRedesign: state.app.canAccessDashboardRedesign,
      constellationScreenSize: state.app.constellationScreenSize,
      isPostingPathAdmin: state.user.isPostingPathAdmin,
      user: state.user,
      isScreenXlGTE: AppSelectors.makeIsScreenXlGTE(state),
      isScreenLg: AppSelectors.makeIsScreenLg(state),
      isRunningInProd: AppSelectors.makeIsRunningInProd(state),
      searchText,
      includeActive: apiParams.includeActive,
      includeInactive: apiParams.includeInactive,
      includeArchived: apiParams.includeArchived,
      includeUnarchived: apiParams.includeUnarchived,
    };
  }, shallowEqual);

  // Extract fields from ListingGroup based on type. Multiplex groups have no listingAlias (use groupId for API calls).
  const listingAlias = listing.type === AccountViewListingGroupType.MULTIPLEX ? '' : listing.listingAlias;
  const name = listing.name;
  const listingStatusText = listing.status?.text || '';
  const listingStatus = listing.status?.status || '';
  const isMultifamily = listing.isMultifamily || false;
  const groupId = listing.groupId;
  const address = listing.address;

  // Get photo URL from photoKey
  const thumb = listing.photoKey ? getPhotoUrls(isRunningInProd, listing.photoKey).thumb : '';
  const listingStatusTagAppearance = ''; // TODO: derive from status if needed
  const dateLastActivatedTimestamp = 0; // TODO: get from API if needed
  const userManagementCompanyId = undefined; // TODO: get from API if needed

  // TODO: not sure if we need this
  const panels: Array<PanelProps> = [];

  const { listingCount = { total: 0, active: 0 } } = listing;

  const fullAddress = address.unit ? `${address.street} #${address.unit}` : address.street || name;
  const shouldHideColumnContent =
    listingStatus === AccountViewListingStatus.ARCHIVED || listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH;

  const displayManagePermissions =
    user.roleTypes?.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN) &&
    user.userManagementCompanyId === userManagementCompanyId;

  const isParentPropertyRow = listing.type === AccountViewListingGroupType.MULTIPLEX;
  const isAGroupUnitPropertyRow = listing.groupId !== '' && listing.groupId !== listing.listingAlias;

  const { data: paymentsData } = useGetPropertiesPayment({
    groupId,
    listingAlias,
    searchText,
    includeActive,
    includeInactive,
    includeArchived,
    includeUnarchived,
  });

  const { data: inquiriesData, refetch: refetchInquiries } = useGetPropertiesInquiries(
    {
      groupId,
      listingAlias,
      searchText,
      includeActive,
      includeInactive,
      includeArchived,
      includeUnarchived,
    },
    { enabled: !shouldHideColumnContent },
  );
  const { data: leasesData } = useGetPropertyLeases(
    {
      groupId,
      listingAlias,
      searchText,
      includeActive,
      includeInactive,
      includeArchived,
      includeUnarchived,
    },
    { enabled: !shouldHideColumnContent },
  );

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get({
    listingAlias,
    includeLeases: true,
  });

  const { mutateAsync: setIsApplicationsAccepted } =
    useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted();

  const panelsToRender = useMemo(() => {
    const panelsToAdd: Array<PanelProps | null> = canAccessDashboardRedesign
      ? [
          PropertyPanel({
            id: dateLastActivatedTimestamp ? PANEL_ID.EDIT_LISTING : PANEL_ID.LIST,
            title: dateLastActivatedTimestamp ? 'Edit listing' : 'List',
            urlPath: `${paths.propertiesPage}/${listingAlias}/${
              dateLastActivatedTimestamp || isMultifamily ? 'listing' : 'create-listing'
            }`,
          }),
          isMultifamily
            ? null
            : PropertyPanel({
                id: PANEL_ID.OVERVIEW,
                title: 'Overview',
                urlPath: `${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`,
              }),
          displayManagePermissions
            ? PropertyPanel({
                id: PANEL_ID.MANAGE_PERMISSIONS,
                title: 'Manage Permissions',
                urlPath: `${paths.userManagementPage}/listing-permissions/${listingAlias}`,
              })
            : null,
        ]
      : [];

    return [...panelsToAdd, ...panels].filter((item) => item && !item.isDisabled);
  }, [panels]);

  const targetListingRoute = useMemo(() => {
    if (isParentPropertyRow) {
      return undefined;
    }

    if (isMultifamily) {
      return `${paths.propertiesPage}/${listingAlias}/listing`;
    }

    const panelToRender = panelsToRender[canAccessDashboardRedesign ? 1 : 0];
    return panelToRender ? panelToRender.urlPath : '';
  }, [canAccessDashboardRedesign, isMultifamily, listingAlias, isParentPropertyRow]);

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.router.updateLocation(pathToRoute, '_blank');
    } else {
      window.router.transitionTo(pathToRoute);
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    PropertiesPageCSEvents.PropertyCardClick({ user });

    dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

    if (isParentPropertyRow && onExpandGroupClick) {
      onExpandGroupClick({ index, groupId });
    }

    if (targetListingRoute) {
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const handleExpandGroupClick = (event?: React.MouseEvent<SVGSVGElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!onExpandGroupClick) return;

    // close any other open menus
    setShowMoreListingAlias('');
    handleClickstreamEvent();
    onExpandGroupClick({ index, groupId });
  };

  const handleClickstreamEvent = () => {
    if (isRowOpen) {
      MultiplexCSEvents.MultiplexCollapseUnits({
        currentProperty: listing,
        user,
      });
    } else {
      MultiplexCSEvents.MultiplexExpandUnits({
        currentProperty: listing,
        user,
      });
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    PropertiesPageCSEvents.PropertyCardOverflowMenu({
      user,
      currentProperty: webAnalyticsData,
      lease,
    });
  };

  const handleMenuItemClick = ({ urlPath: url = '', id = '' }: PanelProps, event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (id === PANEL_ID.OVERVIEW) {
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));
    }

    const openNewTab = event.ctrlKey || event.metaKey;

    if (escapeRoute(id)) {
      if (openNewTab) {
        window.router.updateLocation(url);
      } else {
        window.router.updateLocation(url, '_blank');
      }
    } else {
      routerHandler(url, openNewTab);
    }
  };

  const handleCellCtaClick = (
    event: React.MouseEvent<HTMLElement>,
    cta: Partial<AccountViewTableCellCtaDto>,
    label: string,
  ) => {
    event.stopPropagation();

    if (cta.text === 'Enable applications') {
      event.preventDefault();
      setDisplayApplicationsModal(true);
    }

    switch (label) {
      case PROPERTY_TABLE_COLUMNS.RENT:
        PropertiesPageCSEvents.PropertyCardPayments({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS:
        PropertiesPageCSEvents.PropertyCardPotentialTenants({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.LEASES:
        PropertiesPageCSEvents.PropertyCardLeases({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS
            ] || cta.text,
        });
        if (cta.text === 'View lease') {
          ManageTenantsCSEvents.ViewGeneratedLease({
            currentProperty: listing,
            user,
          });
        }
        break;
      default:
        break;
    }
  };

  const onEnableApplicationsClick = async () => {
    try {
      await setIsApplicationsAccepted({ getParams: { propertyId: listingAlias, isApplicationsAccepted: true } });
      await refetchInquiries();
      enqueueToast(
        <Toast tone="success">
          Applications are enabled for {fullAddress}. You can change application preferences anytime on the
          property&apos;s&nbsp;
          <em>Applications</em>
          &nbsp;tab.
        </Toast>,
      );
    } catch (error) {
      enqueueToast(<Toast tone="critical">There was an error enabling applications. Please try again.</Toast>);
    }
  };

  const getWrappedText = (value: string) => {
    let characterLimit = 30;
    characterLimit = isScreenXlGTE ? characterLimit : 23;
    const finalCharacterLimit = value.length > characterLimit ? characterLimit - 3 : characterLimit + 1;

    return stringHelper.wrapText(value, finalCharacterLimit);
  };

  const getPhotoContent = () => (
    <Box data-testid="PhotoContainer" css={propertyPhotoContainerStyles}>
      <Box
        css={propertyPhotoRecipe.raw({ hasPhoto: !!thumb })}
        style={{ backgroundImage: `url(${thumb ? thumb : MidwestStyleHouse2x})` }}
      />
    </Box>
  );

  const getSingleSummaryStatusContent = () => (
    <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
      <p>
        {listingStatusTagAppearance ? (
          <Tag
            size="sm"
            tone={
              listingStatusTagAppearance === 'error'
                ? 'critical'
                : (listingStatusTagAppearance as TagPropsInterface['tone'])
            }
            icon={listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH ? <IconWarningFilled /> : null}
            css={tagStyles}
          >
            {listingStatusText.toUpperCase()}
          </Tag>
        ) : (
          <Text textStyle="body-sm" color="text.subtle" css={singleTextLineStyles}>
            {listingStatusText}
          </Text>
        )}
      </p>
    </Text>
  );

  const getGroupSummaryContent = () => {
    return (
      <Box css={{ marginTop: '200' }}>
        <Tag size="sm" tone="blue" css={{ marginRight: '200' }}>
          {listingCount.total} units
        </Tag>

        {listingCount.active > 0 && (
          <Tag size="sm" tone="gray">
            {listingCount.active} Listed for rent
          </Tag>
        )}
      </Box>
    );
  };

  const getCellText = ({ status, description, cta }: Partial<AccountViewTableCellDto>, label: string) => (
    <>
      {status &&
        (status.tagAppearance ? (
          <Tag
            size="sm"
            tone={status.tagAppearance === 'error' ? 'critical' : (status.tagAppearance as TagPropsInterface['tone'])}
            css={tagStyles}
          >
            {status.text.toUpperCase()}
          </Tag>
        ) : (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {status.url ? (
              <Anchor
                href={status.url}
                onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
                css={cellAnchorStyles}
              >
                {status.text}
              </Anchor>
            ) : (
              <Text textStyle="body-sm" asChild css={{ textWrap: 'balance' }}>
                <p>{status.text}</p>
              </Text>
            )}
            {status.hasUnread && (
              <Icon
                render={<IconCircleFilled />}
                size="sm"
                css={{ marginLeft: '200', color: 'icon.feedback.notify.hero', width: '200' }}
              />
            )}
          </Box>
        ))}
      {description && (
        <Text textStyle="body-sm" css={{ color: status ? 'text.subtle' : 'text.neutral' }} asChild>
          <p>{description}</p>
        </Text>
      )}
      {cta && (
        <TextButton
          asChild={!!cta.url}
          onClick={(event: React.MouseEvent<HTMLElement>) => handleCellCtaClick(event, cta, label)}
          textStyle="body-sm-bold"
          data-eventid={cta.text === 'View lease' ? ManageTenantsCSEvents.ViewGeneratedLease.data_event_id : undefined}
        >
          {cta.url ? <a href={cta.url || ''}>{cta.text}</a> : cta.text}
        </TextButton>
      )}
    </>
  );

  const getCellDataEventId = (label: string) => {
    if (label === PROPERTY_TABLE_COLUMNS.RENT) {
      return PropertiesPageCSEvents.PropertyCardPayments.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS) {
      return PropertiesPageCSEvents.PropertyCardPotentialTenants.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.LEASES) {
      return PropertiesPageCSEvents.PropertyCardLeases.data_event_id;
    }
    return undefined;
  };

  const renderCellContent = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto>,
    label: string,
  ) => {
    if (shouldHideColumnContent) {
      return <></>;
    }

    if (!status && !description && !cta) {
      return <>-</>;
    }

    if (onlyDisplayOnHover) {
      return (
        <>
          <Box
            data-eventdescription={cta?.text}
            data-eventid={getCellDataEventId(label)}
            css={{ display: 'inline', 'td:hover &': { display: 'none' } }}
          >
            -
          </Box>
          <Box css={{ display: 'none', 'td:hover &': { display: 'block' } }}>
            {getCellText({ status, description, cta }, label)}
          </Box>
        </>
      );
    }

    return getCellText({ status, description, cta }, label);
  };

  const renderCell = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto> = {},
    label: string,
  ) => {
    return (
      <HiddenMobileTableCell
        id={label === PROPERTY_TABLE_COLUMNS.LEASES ? 'PropertyRow-Lease-Cell' : undefined}
        data-eventdescription="Property card hover"
        data-eventid={PropertiesPageCSEvents.PropertyCardHover.data_event_id}
        aria-labelledby={label}
        onMouseOver={() => {
          if (listingAlias && onlyDisplayOnHover) {
            onUpsellCellHover(label, webAnalyticsData, lease);
          }
        }}
        css={{
          backgroundColor: isAGroupUnitPropertyRow || isParentPropertyRow ? `{colors.bg.softest}` : undefined,
        }}
      >
        {renderCellContent({ status, description, cta, onlyDisplayOnHover }, label)}
      </HiddenMobileTableCell>
    );
  };

  // skeleton row for loading state
  if (isLoading) {
    const nestRowBackgroundColor = isAGroupUnitPropertyRow || isParentPropertyRow ? `{colors.bg.softest}` : undefined;
    const nestCellStyle = { backgroundColor: nestRowBackgroundColor };

    return (
      <Table.Row data-testid="GroupablePropertyRowSkeleton" css={{ verticalAlign: 'middle' }}>
        <TableCell
          css={{
            borderLeft: isAGroupUnitPropertyRow ? `4px solid {colors.blue.400}` : undefined,
            backgroundColor: nestRowBackgroundColor,
            ...leftDataCellStyles(isScrolledToLeft),
          }}
        >
          <Box css={cellAddressContainer}>
            <Box css={propertyPhotoContainerStyles}>
              <LoadingImage isLoading loadingWidth="100px" loadingHeight="60px" css={{ borderRadius: '200' }} />
            </Box>
            <Box css={{ ...flexContainerStyles, width: '100%' }}>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', width: '100%' }}>
                <LoadingText isLoading loadingWidth="60%">
                  {null}
                </LoadingText>
                <LoadingText isLoading loadingWidth="40%">
                  {null}
                </LoadingText>
              </Box>
            </Box>
          </Box>
        </TableCell>
        <HiddenMobileTableCell css={nestCellStyle}>
          <LoadingText isLoading loadingWidth="60%">
            {null}
          </LoadingText>
        </HiddenMobileTableCell>
        <HiddenMobileTableCell css={nestCellStyle}>
          <LoadingText isLoading loadingWidth="50%">
            {null}
          </LoadingText>
        </HiddenMobileTableCell>
        <HiddenMobileTableCell css={nestCellStyle}>
          <LoadingText isLoading loadingWidth="55%">
            {null}
          </LoadingText>
        </HiddenMobileTableCell>
        <TableCell css={{ ...rightDataCellStyles, ...nestCellStyle }}>
          <Box css={{ display: 'flex', justifyContent: 'flex-end', marginRight: '400' }}>
            <LoadingImage isLoading loadingWidth="24px" loadingHeight="24px" css={{ borderRadius: '9999px' }} />
          </Box>
        </TableCell>
      </Table.Row>
    );
  }

  return (
    <>
      <Anchor
        href={targetListingRoute}
        onClick={handleRowClick}
        css={{
          display: 'contents',
          textDecoration: 'none',
        }}
      >
        {/* we need to keep onClick handler or this will always reload the page  */}
        <Table.Row
          id={index != null ? `GroupablePropertyRow-${index}` : undefined}
          interactive={true}
          onClick={handleRowClick}
          data-index={index.toString()}
          data-testid="GroupablePropertyRow"
          data-eventid={PropertiesPageCSEvents.PropertyCardClick.data_event_id}
          data-eventdescription="Property card click"
          css={{
            verticalAlign: 'middle',
            '& td': {
              cursor: 'pointer',
            },
          }}
          style={rowStyle}
        >
          <TableCell
            data-testid="GroupablePropertyRow-CellAddress"
            aria-labelledby={PROPERTY_TABLE_COLUMNS.ADDRESS}
            css={{
              borderLeft: isAGroupUnitPropertyRow ? `4px solid {colors.blue.400}` : undefined,
              backgroundColor: isAGroupUnitPropertyRow || isParentPropertyRow ? `{colors.bg.softest}` : undefined,
              ...leftDataCellStyles(isScrolledToLeft),
            }}
          >
            <Box
              id={index != null ? `GroupablePropertyRow-${index}-Cell-0` : undefined}
              css={{
                ...cellAddressContainer,
              }}
              data-testid="GroupablePropertyRow-CellAddressContainer"
            >
              {isParentPropertyRow === false && getPhotoContent()}

              <Box css={{ ...flexContainerStyles, ...minWidthZero }}>
                {/* additional spacing for parent property row */}
                <Box css={{ marginLeft: isParentPropertyRow ? '400' : '0' }} />

                <Box data-testid="AddressAndSummaryContainer" css={minWidthZero}>
                  <Box data-testid="Address">
                    {isPostingPathAdmin ? (
                      <Box>
                        <Text
                          textStyle="body-sm-bold"
                          asChild
                          css={{ ...singleTextLineStyles, maxWidth: textMaxWidth }}
                        >
                          <p>{name}</p>
                        </Text>
                      </Box>
                    ) : (
                      <Text textStyle="body-sm-bold" asChild css={{ ...singleTextLineStyles, maxWidth: textMaxWidth }}>
                        <p>{getWrappedText(name || fullAddress)}</p>
                      </Text>
                    )}
                  </Box>

                  <Box data-testid="Summary" css={{ ...minWidthZero }}>
                    {isParentPropertyRow ? (
                      <>
                        <Box data-testid="GroupablePropertyRow-Status">{getGroupSummaryContent()}</Box>
                      </>
                    ) : (
                      <>
                        <Box data-testid="GroupablePropertyRow-Status">{getSingleSummaryStatusContent()}</Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </TableCell>

          {renderCell(paymentsData as Partial<AccountViewTableCellDto>, PROPERTY_TABLE_COLUMNS.RENT)}
          {renderCell(inquiriesData, PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS)}
          {renderCell(leasesData, PROPERTY_TABLE_COLUMNS.LEASES)}
          <TableCell
            aria-labelledby={PROPERTY_TABLE_COLUMNS.ACTIONS}
            css={{
              ...rightDataCellStyles,
              backgroundColor: isAGroupUnitPropertyRow || isParentPropertyRow ? '{colors.bg.softest}' : undefined,
            }}
          >
            <Box
              css={{
                display: 'flex',
                gap: '200',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {isParentPropertyRow && onExpandGroupClick ? (
                <Icon
                  data-eventid={
                    isRowOpen
                      ? MultiplexCSEvents.MultiplexCollapseUnits.data_event_id
                      : MultiplexCSEvents.MultiplexExpandUnits.data_event_id
                  }
                  title={isRowOpen ? 'Collapse group' : 'Expand group'}
                  role="img"
                  size="md"
                  render={isRowOpen ? <IconChevronUpCircleFilled /> : <IconChevronDownCircleFilled />}
                  onClick={(event: React.MouseEvent<SVGSVGElement>) => !isFetching && handleExpandGroupClick(event)}
                  aria-disabled={isFetching}
                  css={{
                    color: 'icon.accent.brand.hero',
                    cursor: isFetching ? 'not-allowed' : 'pointer',
                    opacity: isFetching ? 0.5 : 1,
                    pointerEvents: isFetching ? 'none' : 'auto',
                    marginRight: '400',
                  }}
                />
              ) : !isParentPropertyRow ? (
                <Menu
                  open={showMoreListingAlias === listingAlias}
                  onOpenChange={(open) => setShowMoreListingAlias(open ? listingAlias : '')}
                  content={panelsToRender.map(
                    (item) =>
                      item && (
                        <Menu.Item
                          key={item.id}
                          onClick={(event: React.MouseEvent<HTMLElement>) =>
                            handleMenuItemClick(item as PanelProps, event)
                          }
                        >
                          {item.title}
                        </Menu.Item>
                      ),
                  )}
                  trigger={
                    <IconButton
                      title={`Actions for ${name}`}
                      icon={<IconMoreFilled />}
                      emphasis="bare"
                      css={{ color: 'icon.accent.blue.hero', width: '1200' }}
                      onClick={handleMenuClick}
                      data-eventid={PropertiesPageCSEvents.PropertyCardOverflowMenu.data_event_id}
                    />
                  }
                />
              ) : null}
            </Box>
          </TableCell>
        </Table.Row>
      </Anchor>
      {displayApplicationsModal && (
        <EnableApplicationsModal
          setShowEnableApplicationsModal={setDisplayApplicationsModal}
          onConfirm={onEnableApplicationsClick}
        />
      )}
    </>
  );
};

export { GroupablePropertyRow };
export type { GroupablePropertyRowProps };
export default GroupablePropertyRow;
